import {
    Button,
    Grid,
    IconButton,
    Box,
    TextField,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TablePagination,
} from "@mui/material";
import { Add, Edit } from "@mui/icons-material";
import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useForm } from "react-hook-form";
import Table from "components/Table";
import Loader from "components/Loader";
import FormDate from "components/FormFields/FormDate";
import { snack } from "components/toast";
import { AxiosError } from "axios";

import {
    getWhatsNew,
    createWhatsNew,
    updateWhatsNew,
} from "api/services/whatsNew";

function useDebounce(value: string, delay = 400) {
    const [debounced, setDebounced] = useState(value);

    useEffect(() => {
        const timer = setTimeout(() => setDebounced(value), delay);
        return () => clearTimeout(timer);
    }, [value, delay]);

    return debounced;
}


function WhatsNew() {
    const queryClient = useQueryClient();

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [search, setSearch] = useState("");
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingItem, setEditingItem] = useState<any>(null);
    const debouncedSearch = useDebounce(search, 400);

    const { control, reset, setValue, watch } = useForm({
        defaultValues: {
            title: "",
            description: "",
            date: "",
        },
    });

    const { data, isLoading } = useQuery(
        ["whats-new", debouncedSearch],
        () =>
            getWhatsNew({
                limit: 100,
                offset: 0,
                search: debouncedSearch || undefined,
            }),

        {
            onError: () => snack.error("Failed to fetch data"),
            keepPreviousData: true,
        }
    );


    const items = data?.result ?? [];


    const { mutate: createMutation, isLoading: isCreating } = useMutation(
        createWhatsNew,
        {
            onSuccess: () => {
                snack.success("Item created");
                setCreateDialogOpen(false);
                reset();
                queryClient.invalidateQueries("whats-new");
            },
            onError: (err: AxiosError<any>) => {
                snack.error(err.response?.data?.message || "Error creating item");
            },
        }
    );

    const handleCreate = () => {
        const values = watch();

        if (!values.title) return snack.error("Title is required");
        if (!values.date) return snack.error("Date is required");

        createMutation(values);
    };


    const { mutate: updateMutation, isLoading: isUpdating } = useMutation(
        updateWhatsNew,
        {
            onSuccess: () => {
                snack.success("Item updated");
                setEditDialogOpen(false);
                setEditingItem(null);
                queryClient.invalidateQueries("whats-new");
            },
            onError: (err: AxiosError<any>) => {
                snack.error(err.response?.data?.message || "Error updating item");
            },
        }
    );

    const openEditDialog = (item) => {
        setEditingItem(item);
        setValue("title", item.title);
        setValue("description", item.description);
        setValue("date", item.date);
        setEditDialogOpen(true);
    };

    const handleUpdate = () => {
        const values = watch();

        if (!values.title) return snack.error("Title is required");
        if (!values.date) return snack.error("Date is required");

        updateMutation({
            id: editingItem.id,
            ...values,
        });
    };


    const paginatedData = useMemo(
        () =>
            items.slice(
                page * rowsPerPage,
                page * rowsPerPage + rowsPerPage
            ),
        [items, page, rowsPerPage]
    );

    const columns = useMemo(
        () => [
            { title: "Title", key: "title" },
            { title: "Description", key: "description" },
            {
                title: "Date",
                key: "date",
                render: (item) =>
                    item.date ? new Date(item.date).toLocaleDateString() : "—",
            },
            {
                title: "Edit",
                key: "edit",
                render: (item) => (
                    <IconButton size="small" onClick={() => openEditDialog(item)}>
                        <Edit fontSize="small" />
                    </IconButton>
                ),
            },
        ],
        []
    );

    if (isLoading) return <Loader />;

    return (
        <Grid container spacing={3} sx={{ width: "98%", margin: "auto", mt: 6 }}>
            <Grid
                item
                xs={12}
                container
                alignItems="center"
                justifyContent="space-between"
            >
                {/* LEFT: Search */}
                <Grid item xs={12} sm={6} md={4}>
                    <TextField
                        placeholder="Search by Title | Description"
                        size="small"
                        fullWidth
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setPage(0);
                        }}
                    />
                </Grid>

                {/* RIGHT: Button */}
                <Grid item xs="auto">
                    <Button
                        startIcon={<Add />}
                        variant="contained"
                        color="secondary"
                        onClick={() => {
                            reset();
                            setCreateDialogOpen(true);
                        }}
                    >
                        Add What’s New
                    </Button>
                </Grid>
            </Grid>


            <Grid item xs={12}>
                <Table columns={columns} data={paginatedData} loading={isLoading} />
                <TablePagination
                    rowsPerPageOptions={[10, 25, 50]}
                    component="div"
                    count={items.length}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                />
            </Grid>

            <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} fullWidth maxWidth="md">
                <DialogTitle>Add What’s New</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} mt={2}>
                        <Grid item xs={8} sm={8} md={8} lg={8}>
                            <TextField label="Title" fullWidth size="small" {...control.register("title")} />
                        </Grid>
                        <Grid item xs={4} sm={4} md={4} lg={4}>
                            <FormDate label="Date" name="date" control={control} />
                        </Grid>
                        <Grid item xs={12} sm={12} md={12} lg={12}>
                            <TextField
                                label="Description"
                                fullWidth
                                multiline
                                rows={3}
                                size="small"
                                {...control.register("description")}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleCreate} disabled={isCreating}>
                        {isCreating ? "Saving..." : "Save"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} fullWidth maxWidth="md">
                <DialogTitle>Edit What’s New</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} mt={2}>
                        <Grid item xs={8} sm={8} md={8} lg={8}>
                            <TextField label="Title" fullWidth size="small" {...control.register("title")} />
                        </Grid>
                        <Grid item xs={4} sm={4} md={4} lg={4}>
                            <FormDate label="Date" name="date" control={control} />
                        </Grid>
                        <Grid item xs={12} sm={12} md={12} lg={12}>
                            <TextField
                                label="Description"
                                fullWidth
                                multiline
                                rows={3}
                                size="small"
                                {...control.register("description")}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleUpdate} disabled={isUpdating}>
                        {isUpdating ? "Saving..." : "Save"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Grid>
    );
}

export default WhatsNew;
